import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import './MisPedidosPage.css';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.origin.includes('localhost') ? 'http://localhost:8080/api' : `${window.location.origin}/api`);

const SeguimientoPedido = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No se proporcionó un token de seguimiento.');
      setLoading(false);
      return;
    }

    const fetchPedido = async () => {
      try {
        const resp = await fetch(`${API_URL}/pedidos/track?token=${encodeURIComponent(token)}`);
        if (resp.status === 404) {
          setError('No se encontró ningún pedido con este enlace.');
          return;
        }
        if (!resp.ok) throw new Error('Error al consultar el pedido');
        const data = await resp.json();
        setPedido(data);
      } catch (err) {
        setError('No se pudo cargar la información del pedido. Intenta de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchPedido();
  }, [token]);

  const getEstadoBadge = (estado) => {
    const config = {
      PENDIENTE: { label: 'Pendiente', icon: '⏳', cls: 'badge-pendiente' },
      PAGADO:    { label: 'Pagado',    icon: '✅', cls: 'badge-pagado' },
      ENVIADO:   { label: 'Enviado',   icon: '🚚', cls: 'badge-enviado' },
      ENTREGADO: { label: 'Entregado', icon: '📦', cls: 'badge-entregado' },
      CANCELADO: { label: 'Cancelado', icon: '❌', cls: 'badge-cancelado' },
    };
    const c = config[estado] || { label: estado, icon: '❓', cls: 'badge-pendiente' };
    return <span className={`pedido-badge ${c.cls}`}>{c.icon} {c.label}</span>;
  };

  const getEstadoStep = (estado) => {
    if (estado === 'CANCELADO') return -1;
    return ['PENDIENTE', 'PAGADO', 'ENVIADO', 'ENTREGADO'].indexOf(estado);
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: 'COP',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="pedidos-page">
        <div className="pedidos-container">
          <div className="pedidos-loading">
            <div className="pedidos-spinner"></div>
            <p>Consultando tu pedido...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pedidos-page">
        <div className="pedidos-container">
          <div className="pedidos-header">
            <h1>Seguimiento de Pedido</h1>
          </div>
          <div className="pedidos-error" style={{ flexDirection: 'column', gap: '1rem' }}>
            <p style={{ margin: 0 }}>{error}</p>
            <Link to="/productos" style={{ color: 'var(--color-accent-light)', fontWeight: 600 }}>
              Volver a la tienda
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const step = getEstadoStep(pedido.estado);

  return (
    <div className="pedidos-page">
      <div className="pedidos-container">
        <div className="pedidos-header">
          <h1>Seguimiento de Pedido</h1>
          <p className="pedidos-subtitle">Consulta el estado de tu compra</p>
        </div>

        <div className="pedido-card expanded">
          <div className="pedido-card-header" style={{ cursor: 'default' }}>
            <div className="pedido-info-row">
              <div className="pedido-id">
                <span className="pedido-label">Pedido</span>
                <span className="pedido-number">#{pedido.id}</span>
              </div>
              <div className="pedido-fecha">{formatDate(pedido.creado_en)}</div>
            </div>
            <div className="pedido-info-row">
              <div className="pedido-total">{formatCurrency(pedido.total)}</div>
              <div className="pedido-estado-badge">{getEstadoBadge(pedido.estado)}</div>
            </div>
          </div>

          <div className="pedido-detail">
            {pedido.estado === 'CANCELADO' ? (
              <div className="pedido-cancelled-notice">
                <span className="cancelled-icon">❌</span>
                <p>Este pedido ha sido <strong>cancelado</strong>.</p>
              </div>
            ) : (
              <div className="pedido-progress">
                {['Pendiente', 'Pagado', 'Enviado', 'Entregado'].map((label, i) => (
                  <div key={label} className={`progress-step ${i <= step ? 'active' : ''} ${i === step ? 'current' : ''}`}>
                    <div className="step-dot">
                      {i < step ? '✓' : i === step ? (['⏳', '✅', '🚚', '📦'][i]) : ''}
                    </div>
                    <span className="step-label">{label}</span>
                  </div>
                ))}
                <div className="progress-line">
                  <div className="progress-fill" style={{ width: `${(step / 3) * 100}%` }}></div>
                </div>
              </div>
            )}

            <div className="pedido-items-list">
              <h4>Productos</h4>
              {pedido.items?.map((item, idx) => (
                <div key={idx} className="pedido-item">
                  <div className="item-img-wrapper">
                    {item.imagen ? (
                      <img src={item.imagen} alt={item.nombre} className="item-img"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div className="item-img-placeholder" style={item.imagen ? { display: 'none' } : {}}>🎲</div>
                  </div>
                  <div className="item-info">
                    <span className="item-name">{item.nombre || `Producto #${item.producto_id}`}</span>
                    <span className="item-qty">Cant: {item.cantidad} × {formatCurrency(item.precio_unitario)}</span>
                  </div>
                  <div className="item-subtotal">{formatCurrency(item.subtotal)}</div>
                </div>
              ))}
            </div>

            <div className="pedido-summary">
              {pedido.subtotal > 0 && (
                <div className="summary-row">
                  <span>Subtotal productos</span>
                  <span>{formatCurrency(pedido.subtotal)}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Envío</span>
                <span style={{ color: pedido.costo_envio === 0 ? 'var(--color-success)' : 'inherit' }}>
                  {pedido.costo_envio === 0 ? 'Gratis' : formatCurrency(pedido.costo_envio)}
                </span>
              </div>
              {pedido.discount_amount > 0 && (
                <div className="summary-row" style={{ color: 'var(--color-success)' }}>
                  <span>Descuento {pedido.coupon_code ? `(${pedido.coupon_code})` : ''}</span>
                  <span>-{formatCurrency(pedido.discount_amount)}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatCurrency(pedido.total)}</span>
              </div>
            </div>

            {(pedido.direccion_envio || pedido.ciudad) && (
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--color-surface-2)', borderRadius: '8px', fontSize: '0.9rem', color: 'var(--color-muted)' }}>
                <strong>Dirección de entrega</strong>
                <p style={{ margin: '0.4rem 0 0' }}>
                  {[pedido.direccion_envio, pedido.ciudad, pedido.departamento].filter(Boolean).join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Link to="/productos" style={{ color: 'var(--color-accent-light)', fontWeight: 600, textDecoration: 'none' }}>
            ← Volver a la tienda
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SeguimientoPedido;
